/**
 * =============================================================================
 * Hook: useActivitiesRealtime
 * Versão: 1.0.0 (2026-05-08) — item 3.10 do Playbook de Vendas
 *
 * Descrição:
 *   Centraliza assinaturas realtime das tabelas que alimentam a listagem
 *   unificada de atividades:
 *     - public.crm_deal_activities      (atividades avulsas)
 *     - public.crm_deal_cadence_steps   (passos de cadência)
 *
 *   Em qualquer INSERT/UPDATE/DELETE em qualquer das duas tabelas, invalida
 *   as queries `['activities-list']` e `['activities-kpis']` do TanStack
 *   Query, garantindo que KPIs e tabela permaneçam sincronizados sem
 *   necessidade de refetch manual.
 *
 *   Padrão segue Core rule do projeto: realtime centralizado por página
 *   invalidando cache do TanStack Query (não atualiza diretamente o estado).
 * =============================================================================
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('ActivitiesRealtime');

/**
 * Assina mudanças nas tabelas de atividades e cadência e invalida o
 * cache da listagem unificada `/tenant/activities/list`.
 *
 * Deve ser chamado UMA vez na página `ActivitiesList`.
 */
export function useActivitiesRealtime(tenantId: string | null | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!tenantId) return;

    // Helper de invalidação — chamado por todos os eventos
    const invalidate = (source: string) => {
      logger.debug('Invalidando cache de atividades', { source });
      queryClient.invalidateQueries({ queryKey: ['activities-list'] });
      queryClient.invalidateQueries({ queryKey: ['activities-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['activities-kpis'] });
      queryClient.invalidateQueries({ queryKey: ['activities-type-counts'] });
      // v1.1.0 — Mantém o card "Total" do dashboard Minhas Atividades em sincronia
      queryClient.invalidateQueries({ queryKey: ['my-activities-overview'] });
      queryClient.invalidateQueries({ queryKey: ['team-activities-overview'] });
    };

    // Canal único para as duas tabelas (reduz overhead de websocket)
    const channel = supabase
      .channel(`activities-realtime-${tenantId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_deal_activities',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => invalidate(`crm_deal_activities:${payload.eventType}`),
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_deal_cadence_steps',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => invalidate(`crm_deal_cadence_steps:${payload.eventType}`),
      )
      .subscribe((status) => {
        logger.debug('Status do canal realtime', { status });
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tenantId, queryClient]);
}
